import { useEffect, useRef, useState } from "react";
import { IonButton, IonModal, IonSpinner, useIonToast } from "@ionic/react";

import PurchaseTermsConsent from "../PurchaseTermsConsent";
import LegalDocumentModal from "../LegalDocumentModal";
import {
  recordStorePurchaseConsent,
  takePurchaseRefusalMessage,
} from "../../services/purchaseConsents";
import "./StorePurchaseConfirmSheet.css";

const REQUEST_EVENT = "storepurchaseconfirmrequest";

/**
 * Ask the member to confirm a store purchase started from the Shop.
 *
 * "Abbonati ora" in the Shop used to open the Apple / Google sheet on the first
 * tap. It now opens this sheet first, with the Termini e Condizioni /
 * Informativa Privacy confirmation (client, 02/10 and 05/10/2026); the store
 * sheet follows only once it is ticked and recorded.
 *
 * Resolves true to go ahead, false when the member backs out.
 *
 * @param {{ productId: string, title?: string, price?: string }} purchase
 * @returns {Promise<boolean>}
 */
export const confirmStorePurchase = (purchase) =>
  new Promise((resolve) => {
    globalThis.dispatchEvent(
      new CustomEvent(REQUEST_EVENT, { detail: { ...purchase, resolve } }),
    );
  });

/** Mounted once in App; answers confirmStorePurchase(). */
const StorePurchaseConfirmSheet = () => {
  const [request, setRequest] = useState(null);
  const [termsAccepted, setTermsAccepted] = useState(false);
  const [showTermsError, setShowTermsError] = useState(false);
  const [saving, setSaving] = useState(false);
  const [legalSlug, setLegalSlug] = useState(null);
  const resolveRef = useRef(null);
  const [toast] = useIonToast();

  useEffect(() => {
    const onRequest = (event) => {
      // A request still open is abandoned in favour of the new one.
      resolveRef.current?.(false);
      resolveRef.current = event.detail?.resolve ?? null;
      setTermsAccepted(false);
      setShowTermsError(false);
      setRequest(event.detail ?? null);
    };
    globalThis.addEventListener(REQUEST_EVENT, onRequest);
    return () => globalThis.removeEventListener(REQUEST_EVENT, onRequest);
  }, []);

  const finish = (confirmed) => {
    const resolve = resolveRef.current;
    resolveRef.current = null;
    setRequest(null);
    resolve?.(confirmed);
  };

  const onContinue = async () => {
    if (!request || saving) return;
    if (!termsAccepted) {
      setShowTermsError(true);
      return;
    }
    setSaving(true);
    try {
      const kept = await recordStorePurchaseConsent(request.productId);
      if (!kept) {
        toast({
          message:
            takePurchaseRefusalMessage() ??
            "Non è stato possibile registrare la conferma. Riprova.",
          duration: 5000,
        });
        return;
      }
      finish(true);
    } finally {
      setSaving(false);
    }
  };

  return (
    <>
      <IonModal
        isOpen={!!request}
        className="store-purchase-confirm"
        initialBreakpoint={0.6}
        breakpoints={[0, 0.6, 1]}
        onDidDismiss={() => {
          // Swiped down or closed: not a purchase.
          if (resolveRef.current) finish(false);
        }}
      >
        <div className="store-purchase-confirm__body">
          <h2 className="store-purchase-confirm__title">Conferma il tuo abbonamento</h2>
          {request?.title || request?.price ? (
            <p className="store-purchase-confirm__product">
              {request?.title}
              {request?.title && request?.price ? " — " : ""}
              {request?.price}
            </p>
          ) : null}

          <PurchaseTermsConsent
            checked={termsAccepted}
            onChange={(checked) => {
              setTermsAccepted(checked);
              if (checked) setShowTermsError(false);
            }}
            showError={showTermsError}
            onOpenDocument={setLegalSlug}
          />

          <IonButton
            expand="block"
            className="store-purchase-confirm__continue"
            disabled={saving}
            onClick={onContinue}
          >
            {saving ? <IonSpinner name="dots" /> : "CONTINUA"}
          </IonButton>
          <IonButton
            expand="block"
            fill="clear"
            className="store-purchase-confirm__cancel"
            disabled={saving}
            onClick={() => finish(false)}
          >
            Annulla
          </IonButton>
        </div>
      </IonModal>
      <LegalDocumentModal slug={legalSlug} onClose={() => setLegalSlug(null)} />
    </>
  );
};

export default StorePurchaseConfirmSheet;
